/**
 * The media the music is playing back on, and the wall between it and the
 * player. This is most of the effect: the notes underneath are plain
 * oscillators, and everything that makes them sound like a record heard from
 * two rooms away happens here.
 *
 *   piece voices ─> wall LPF ─┐
 *   surface noise ────────────┼─> dropout ─> air LPF x2 ─> out
 *   crackle ──────────────────┘
 *
 * Four things are going on:
 *
 *   wow and flutter  a slow LFO plus a much slower drift, summed into one
 *                    signal in cents that every oscillator in the piece has
 *                    its `detune` connected to. Shared deliberately — separate
 *                    per-note LFOs would read as chorus, whereas one shared
 *                    one is the whole record sagging together, which is what
 *                    a worn tape actually does.
 *   surface noise    a continuous filtered floor, the sound of the medium.
 *   crackle          sparse pops, pre-rendered once into a loopable buffer.
 *                    One buffer source for a whole piece is far cheaper than
 *                    a node per pop, and at ten pops a second that matters.
 *   dropout          a rare, brief dip in both level and pitch.
 *
 * The wall filter is the reason none of this is bright: a partition passes the
 * bottom two or three hundred hertz and eats the rest, so a stacked chord
 * arrives as its fundamentals and nothing else. The pair of air filters after
 * it is a hard backstop — nothing above roughly 2kHz leaves this chain, so the
 * music can never compete with the fluorescent hum for the top of the mix.
 *
 * A chain is built when a piece starts and torn down when it ends, so the
 * three-to-seven minutes of silence between pieces cost nothing at all.
 */

import type { RNG } from '../core/types';
import type { AudioGraph } from './graph';

export interface TapeProfile {
  /** Wall low-pass in Hz. 250 is through a floor, 1200 is a door ajar. */
  wall: number;
  /** Cents of wow, at `wowRate`. */
  wow: number;
  /** Cents of very slow speed drift, so the piece never returns to true pitch. */
  drift: number;
  /** Wow rate in Hz. Anywhere in 0.3–0.9 reads as a warped tape. */
  wowRate: number;
  /** Level of the continuous surface-noise floor. */
  surface: number;
  /** Level of the crackle pops. */
  crackle: number;
  /** Mean seconds between tape dropouts. 0 disables them. */
  dropoutEvery: number;
  /**
   * Cents the transport loses over one phrase, or omitted for a machine that
   * holds its speed. Unlike `wow` and `drift` this is not an oscillation: only
   * about a third of each sag comes back, so the piece is measurably flatter at
   * the end than it was at the start and there is no point in the phrase where
   * it returns to where it began.
   */
  sag?: number;
  /** Mean seconds between sags. Ignored when `sag` is absent. */
  sagEvery?: number;
  /** How much of the piece goes to the convolver. These want a lot. */
  send: number;
}

/**
 * Seconds of pre-rendered crackle. Long enough that the loop is not audible
 * under everything else, short enough to be worth keeping in memory for the
 * whole session — it is generated once, on the first piece, and reused.
 */
const CRACKLE_SECONDS = 3;

/** Hard ceiling on anything this chain emits. */
const AIR_HZ = 2000;

/**
 * Floor on the accumulated sag, in cents. Roughly a semitone and a quarter,
 * which is as flat as a record can get and still be a record — past this the
 * ear stops hearing a machine in trouble and starts hearing a different piece.
 */
const SAG_FLOOR = -150;

/**
 * Sparse decaying impulses. The gaps are drawn from a wide range so the pops
 * cluster and thin out the way a worn groove does rather than arriving at a
 * steady rate, and one pop in twenty is much louder — a scratch rather than
 * dust.
 */
export function makeCrackleBuffer(ctx: BaseAudioContext, rng: RNG): AudioBuffer {
  const sr = ctx.sampleRate;
  const n = Math.max(1, Math.floor(sr * CRACKLE_SECONDS));
  const buf = ctx.createBuffer(2, n, sr);
  for (let c = 0; c < buf.numberOfChannels; c++) {
    const d = buf.getChannelData(c);
    let i = Math.floor(rng.range(0, sr * 0.1));
    while (i < n) {
      const amp = Math.pow(rng.range(0.2, 1), 2) * (rng.chance(0.05) ? 2.6 : 0.55);
      const len = Math.max(2, Math.floor(rng.range(sr * 0.0004, sr * 0.0026)));
      for (let j = 0; j < len && i + j < n; j++) {
        const fall = 1 - j / len;
        d[i + j]! += (rng.next() * 2 - 1) * amp * fall * fall * fall;
      }
      i += len + Math.floor(rng.range(sr * 0.006, sr * 0.4));
    }
  }
  return buf;
}

export class TapeChain {
  /** Piece voices connect here. Everything past this point is the medium. */
  readonly input: GainNode;
  /** Chain output. Whoever built it owns the level past this node. */
  readonly out: GainNode;

  private readonly ctx: AudioContext;
  private readonly wall: BiquadFilterNode;
  private readonly drop: GainNode;
  /** Sum of every pitch modulation, in cents. Consumers connect it to detune. */
  private readonly warble: GainNode;
  /** Gain of a DC source, used as a writable constant added into `warble`. */
  private readonly dropPitch: GainNode;
  /** The same trick again, kept separate so a dropout cannot undo a sag. */
  private readonly sagPitch: GainNode;
  private readonly started: AudioScheduledSourceNode[] = [];
  private readonly nodes: AudioNode[] = [];
  /** Detune params of live notes, so they can be released on teardown. */
  private readonly attached = new Set<AudioParam>();

  private dropAt: number;
  private sagAt = Infinity;
  /** Cents the transport has lost and not recovered so far. Never positive. */
  private sagFloor = 0;
  private openness = 0;
  private appliedWall = 0;

  constructor(
    private readonly g: AudioGraph,
    private readonly profile: TapeProfile,
    crackleBuffer: AudioBuffer,
    private readonly rng: RNG,
    dest: AudioNode,
  ) {
    const ctx = g.ctx;
    this.ctx = ctx;

    this.out = this.own(ctx.createGain());
    this.out.gain.value = 1;
    this.out.connect(dest);

    // Two poles at 2kHz rather than one: a single 12dB slope still lets enough
    // through at 4kHz for a stacked saw to sound like a synthesiser.
    const air2 = this.own(this.lowpass(AIR_HZ, 0.5));
    air2.connect(this.out);
    const air1 = this.own(this.lowpass(AIR_HZ, 0.7));
    air1.connect(air2);
    // A building passes bass, but not sub-bass with any level; and this layer
    // must never fight the tension bed for the bottom octave.
    const hp = this.own(ctx.createBiquadFilter());
    hp.type = 'highpass';
    hp.frequency.value = 55;
    hp.Q.value = 0.6;
    hp.connect(air1);

    this.drop = this.own(ctx.createGain());
    this.drop.gain.value = 1;
    this.drop.connect(hp);

    this.wall = this.own(this.lowpass(profile.wall, 0.8));
    this.wall.connect(this.drop);
    this.appliedWall = profile.wall;

    this.input = this.own(ctx.createGain());
    this.input.gain.value = 1;
    this.input.connect(this.wall);

    this.warble = this.own(ctx.createGain());
    this.warble.gain.value = 1;
    this.lfo(profile.wowRate, profile.wow, this.warble);
    // Two more, both incommensurate with the first, so the pitch never comes
    // back around to where it started.
    this.lfo(profile.wowRate * 0.37 + 0.011, profile.drift, this.warble);
    this.lfo(5.9 + rng.range(-0.7, 0.7), profile.wow * 0.14, this.warble);
    this.dropPitch = this.constant(0);
    this.dropPitch.connect(this.warble);
    this.sagPitch = this.constant(0);
    this.sagPitch.connect(this.warble);

    this.surfaceNoise();
    this.crackle(crackleBuffer);

    this.dropAt = g.now + rng.range(0.35, 1.1) * Math.max(1, profile.dropoutEvery);
    if (profile.sag) {
      // Not from zero: the machine was already running before the player walked
      // into earshot, so the first sag should not wait a full phrase.
      this.sagAt = g.now + rng.range(0.2, 0.8) * Math.max(4, profile.sagEvery ?? 20);
    }
  }

  /**
   * Give a note's oscillator the same drift as everything else in the piece.
   * The caller must pass the same param to `detach` when the note is released
   * — an incoming connection keeps the target alive, so leaving it attached
   * would pin every note ever played for the length of the piece.
   */
  attach(param: AudioParam): void {
    this.warble.connect(param);
    this.attached.add(param);
  }

  detach(param: AudioParam): void {
    if (!this.attached.delete(param)) return;
    try {
      this.warble.disconnect(param);
    } catch {
      /* already gone */
    }
  }

  /**
   * 0..1 for how open the space around the player is. A big room lets more of
   * the top through, which is a cheap way of making the same piece feel closer
   * in an atrium than in a warren without touching the synthesis at all.
   */
  setOpenness(v: number): void {
    this.openness = v;
    const target = this.profile.wall * (1 + v * 1.2);
    // Only worth an automation event when it has actually moved.
    if (Math.abs(target - this.appliedWall) < this.profile.wall * 0.06) return;
    this.appliedWall = target;
    this.wall.frequency.setTargetAtTime(target, this.g.now, 1.2);
  }

  /** Everything here is scheduled ahead on the audio clock, not fired now. */
  update(now: number): void {
    // A non-finite time reaches `cancelScheduledValues` two calls down and
    // throws, which would take the whole piece with it. Sit the frame out.
    if (!Number.isFinite(now)) return;

    if (now >= this.sagAt) {
      this.scheduleSag(now + this.rng.range(0, 1.5));
      this.sagAt = now + this.rng.range(0.7, 1.4) * Math.max(4, this.profile.sagEvery ?? 20);
    }

    if (this.profile.dropoutEvery <= 0) return;
    if (now < this.dropAt) return;
    this.scheduleDropout(now + this.rng.range(0.05, 0.6));
    this.dropAt = now + this.rng.range(0.6, 1.7) * this.profile.dropoutEvery;
  }

  dispose(): void {
    const t = this.g.now;
    for (const s of this.started) {
      try {
        s.stop(t);
      } catch {
        /* already stopped */
      }
    }
    this.started.length = 0;
    for (const p of this.attached) {
      try {
        this.warble.disconnect(p);
      } catch {
        /* already gone */
      }
    }
    this.attached.clear();
    for (const n of this.nodes) n.disconnect();
    this.nodes.length = 0;
  }

  /**
   * The transport dragging across a phrase and only partly picking itself up.
   *
   * The ramp down takes most of the span and the recovery is deliberately
   * incomplete, so what the ear gets is a piece that is going flat rather than a
   * piece that is wobbling. Held against `SAG_FLOOR` because the accumulation is
   * one-directional and a long performance would otherwise walk it into the
   * basement.
   */
  private scheduleSag(t: number): void {
    const depth = this.rng.range(0.6, 1) * (this.profile.sag ?? 0);
    const span = this.rng.range(4, 9);
    const bottom = Math.max(SAG_FLOOR, this.sagFloor - depth);
    const rest = Math.max(SAG_FLOOR, this.sagFloor - depth * this.rng.range(0.25, 0.55));

    const p = this.sagPitch.gain;
    p.cancelScheduledValues(t);
    p.setValueAtTime(this.sagFloor, t);
    p.linearRampToValueAtTime(bottom, t + span * 0.62);
    p.linearRampToValueAtTime(rest, t + span);
    this.sagFloor = rest;
  }

  /** The head momentarily losing the tape: level and pitch sag together. */
  private scheduleDropout(t: number): void {
    const hold = this.rng.range(0.08, 0.3);
    const depth = this.rng.range(0.18, 0.5);
    const cents = this.rng.range(18, 55);

    const g = this.drop.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(1, t);
    g.linearRampToValueAtTime(depth, t + 0.05);
    g.setValueAtTime(depth, t + hold);
    g.linearRampToValueAtTime(1, t + hold + 0.26);

    const p = this.dropPitch.gain;
    p.cancelScheduledValues(t);
    p.setValueAtTime(0, t);
    p.linearRampToValueAtTime(-cents, t + 0.07);
    p.setValueAtTime(-cents, t + hold);
    p.linearRampToValueAtTime(0, t + hold + 0.34);
  }

  /** The medium itself: a band of noise that is there the whole time. */
  private surfaceNoise(): void {
    if (this.profile.surface <= 0) return;
    const src = this.own(this.ctx.createBufferSource());
    src.buffer = this.g.pink;
    src.loop = true;
    src.playbackRate.value = this.rng.range(0.9, 1.1);
    const bp = this.own(this.ctx.createBiquadFilter());
    bp.type = 'bandpass';
    bp.frequency.value = 780;
    bp.Q.value = 0.5;
    const level = this.own(this.ctx.createGain());
    level.gain.value = this.profile.surface;
    src.connect(bp);
    bp.connect(level);
    // Past the wall filter: the noise is on the record, not on the far side of
    // the plaster, and running it through the wall as well leaves nothing.
    level.connect(this.drop);
    this.lfo(0.047, this.profile.surface * 0.35, level.gain);
    this.start(src, this.rng.range(0, 2));
  }

  /**
   * Pops, band-limited around 1.2kHz. A record heard through a wall does not
   * go "crack", it goes "tick" — the sharp part of a pop is the first thing the
   * building takes away.
   */
  private crackle(buffer: AudioBuffer): void {
    if (this.profile.crackle <= 0) return;
    const src = this.own(this.ctx.createBufferSource());
    src.buffer = buffer;
    src.loop = true;
    src.playbackRate.value = this.rng.range(0.85, 1.15);
    const bp = this.own(this.ctx.createBiquadFilter());
    bp.type = 'bandpass';
    bp.frequency.value = 1200;
    bp.Q.value = 0.8;
    const level = this.own(this.ctx.createGain());
    level.gain.value = this.profile.crackle;
    src.connect(bp);
    bp.connect(level);
    level.connect(this.drop);
    this.start(src, this.rng.range(0, CRACKLE_SECONDS * 0.9));
  }

  /**
   * A gain node holding a writable constant, fed by a one-sample looping DC
   * buffer. `ConstantSourceNode` would do the same job but is missing on older
   * WebKit, and this layer is not worth a feature check.
   */
  private constant(value: number): GainNode {
    const dc = this.ctx.createBuffer(1, 1, this.ctx.sampleRate);
    dc.getChannelData(0)[0] = 1;
    const src = this.own(this.ctx.createBufferSource());
    src.buffer = dc;
    src.loop = true;
    const g = this.own(this.ctx.createGain());
    g.gain.value = value;
    src.connect(g);
    this.start(src);
    return g;
  }

  private lowpass(freq: number, q: number): BiquadFilterNode {
    const f = this.ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = freq;
    f.Q.value = q;
    return f;
  }

  private lfo(rate: number, depth: number, target: AudioNode | AudioParam): void {
    if (depth === 0) return;
    const osc = this.own(this.ctx.createOscillator());
    osc.type = 'sine';
    osc.frequency.value = rate;
    const amp = this.own(this.ctx.createGain());
    amp.gain.value = depth;
    osc.connect(amp);
    // The two branches differ only in which `connect` overload they pick.
    if (target instanceof AudioNode) amp.connect(target);
    else amp.connect(target);
    this.start(osc, 0);
  }

  private own<T extends AudioNode>(node: T): T {
    this.nodes.push(node);
    return node;
  }

  private start(node: AudioScheduledSourceNode, offset = 0): void {
    const t = this.g.now;
    if (node instanceof AudioBufferSourceNode) node.start(t, offset);
    else node.start(t);
    this.started.push(node);
  }
}
